import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Send, CheckCircle, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import FileUploadField from '@/components/FileUploadField';
import { validateFile, RESUME_EXTS, ADDITIONAL_EXTS } from '@/utils/fileValidation';

const API = process.env.REACT_APP_BACKEND_URL;
const INITIAL_FORM = { name: '', email: '', phone: '', message: '' };

function buildFormData({ form, resume, additional, job }) {
  const fd = new FormData();
  fd.append('name', form.name);
  fd.append('email', form.email);
  fd.append('phone', form.phone);
  fd.append('message', form.message);
  fd.append('job_id', job?.id || '');
  fd.append('job_title', job?.title || '');
  fd.append('resume', resume);
  if (additional) fd.append('additional', additional);
  return fd;
}

function validateSubmission({ form, resume, additional }) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Required';
  if (!form.email.trim()) errors.email = 'Required';
  if (!resume) {
    errors.resume = 'Resume is required';
  } else {
    const ferr = validateFile(resume, RESUME_EXTS);
    if (ferr) errors.resume = ferr;
  }
  if (additional) {
    const aerr = validateFile(additional, ADDITIONAL_EXTS);
    if (aerr) errors.additional = aerr;
  }
  return errors;
}

export default function JobApplicationModal({ open, onClose, job }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [resume, setResume] = useState(null);
  const [additional, setAdditional] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (open) {
      setForm(INITIAL_FORM);
      setResume(null);
      setAdditional(null);
      setErrors({});
      setStatus(null);
      setErrorMsg('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleResumeChange = (e) => {
    const f = e.target.files?.[0] || null;
    setResume(f);
    setErrors((p) => ({ ...p, resume: f ? validateFile(f, RESUME_EXTS) : null }));
  };

  const handleAdditionalChange = (e) => {
    const f = e.target.files?.[0] || null;
    setAdditional(f);
    setErrors((p) => ({ ...p, additional: f ? validateFile(f, ADDITIONAL_EXTS) : null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateSubmission({ form, resume, additional });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSubmitting(true);
    setStatus(null);
    setErrorMsg('');
    try {
      await axios.post(`${API}/api/careers/apply`, buildFormData({ form, resume, additional, job }), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.response?.data?.detail || err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="application-modal"
    >
      <div className="bg-[#121212] border border-white/10 w-full max-w-2xl my-8 relative">
        <div className="flex items-start justify-between p-6 border-b border-white/10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#F2B705] font-bold mb-2">Apply for Position</p>
            <h2 className="text-xl font-bold text-white">{job?.title || 'General Application'}</h2>
            {job?.location && <p className="text-xs text-[#A3A3A3] mt-1">{job.location} · {job.employment_type || job.type}</p>}
          </div>
          <button onClick={onClose} data-testid="application-close-btn" className="text-[#888] hover:text-white p-2 -mt-2 -mr-2">
            <X size={20} />
          </button>
        </div>

        {status === 'success' ? (
          <SuccessState onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5" data-testid="application-form">
            {status === 'error' && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Full Name *" error={errors.name}>
                <Input data-testid="app-name" value={form.name} onChange={(e) => update('name', e.target.value)} className="bg-transparent border-white/10 text-white h-11 focus:border-[#F2B705]" />
              </FormField>
              <FormField label="Email *" error={errors.email}>
                <Input data-testid="app-email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="bg-transparent border-white/10 text-white h-11 focus:border-[#F2B705]" />
              </FormField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Phone">
                <Input data-testid="app-phone" type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(555) 555-5555" className="bg-transparent border-white/10 text-white h-11 focus:border-[#F2B705]" />
              </FormField>
              <FormField label="Position">
                <Input value={job?.title || 'General Application'} readOnly className="bg-[#0a0a0a] border-white/10 text-[#A3A3A3] h-11 cursor-not-allowed" />
              </FormField>
            </div>

            <FormField label="Cover Note">
              <Textarea data-testid="app-message" value={form.message} onChange={(e) => update('message', e.target.value)} rows={4} placeholder="Tell us briefly about your relevant experience and why this role interests you." className="bg-transparent border-white/10 text-white text-sm focus:border-[#F2B705] resize-none" />
            </FormField>

            <FileUploadField
              label="Resume / CV *"
              helperText="PDF, DOC, DOCX — max 5 MB"
              file={resume}
              error={errors.resume}
              onChange={handleResumeChange}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              testId="app-resume"
              icon="upload"
            />

            <FileUploadField
              label="Additional Document (optional)"
              file={additional}
              error={errors.additional}
              onChange={handleAdditionalChange}
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
              testId="app-additional"
              icon="file"
            />

            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-3 border-t border-white/10">
              <button type="button" onClick={onClose} className="border border-white/10 text-[#A3A3A3] font-bold text-xs uppercase tracking-wide px-6 py-3 hover:text-white transition-colors">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="app-submit-btn"
                className="flex-1 bg-[#F2B705] text-black font-bold text-xs uppercase tracking-wide px-6 py-3 hover:bg-[#D9A404] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : (<>Submit Application <Send size={14} /></>)}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function FormField({ label, error, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold mb-1.5 block">{label}</Label>
      {children}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function SuccessState({ onClose }) {
  return (
    <div className="p-10 text-center" data-testid="application-success">
      <CheckCircle className="text-[#F2B705] mx-auto mb-4" size={48} />
      <h3 className="text-lg font-bold text-white mb-2">Application Received</h3>
      <p className="text-sm text-[#A3A3A3] max-w-md mx-auto">
        Thanks for applying. We'll review your materials and be in touch shortly at the email you provided.
      </p>
      <button onClick={onClose} className="mt-8 bg-[#F2B705] text-black font-bold text-xs uppercase tracking-wide px-8 py-3 hover:bg-[#D9A404] transition-colors">
        Close
      </button>
    </div>
  );
}
